/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: purchase_orders entity configuration
 * Generator: scripts/generate-api-clients/generate-api-clients.ts
 *
 * This file contains the API client for the purchase_orders table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the purchase_orders API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, organization_id, vendor_id, po_number, order_date, delivery_date, delivery_address, status, subtotal, tax, shipping, total, project_id, job_id, lot_id, notes, created_by, approved_by, approved_at, created_at, updated_at, trig_approved_by_name, vendor_branch_id, trig_vendor_name, trig_vendor_code, trig_vendor_is_active, trig_vendor_contact_name, trig_vendor_contact_email, trig_vendor_contact_phone, trig_vendor_website, trig_branch_name, trig_branch_code, trig_branch_display_name, trig_branch_contact_name, trig_branch_contact_email, trig_branch_contact_phone, trig_branch_address, trig_branch_city, trig_branch_state, trig_branch_zip_code, trig_branch_is_active, trig_branch_is_primary, trig_vendor_branch_name, phase_id, trig_phase_name, trig_phase_description, trig_lot_number, trig_created_by_name, status_id, trig_status_name, trig_status_code, trig_status_color, payment_terms, trig_job_name, trig_job_number, trig_job_builder_name, trig_job_city, auto_serial_number, eta, fees, discounts, tax_rate, department, payment_terms_id, trig_payment_terms_name, trig_payment_terms_code, is_pick_ticket, is_stock, vendor_order_number, fob, ship_via, requested_date]
 * Searchable fields: []
 * Filterable fields: [id, organization_id, vendor_id, project_id, job_id, lot_id, vendor_branch_id, phase_id, status_id, payment_terms_id, is_pick_ticket, is_stock]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig } from '../../../config/search/global-search-config.js';
import { matchesAllTerms } from '../../../config/search/search-utils.js';
import type {
  PurchaseOrdersRow,
  PurchaseOrdersInsert,
  PurchaseOrdersUpdate
} from '../generated_schemas/generated-purchase_orders-schema.js';
import { PurchaseOrdersQueryOptions } from '../generated_schemas/generated-purchase_orders-schema.js';
export type { PurchaseOrdersQueryOptions };

// Internal response type with non-null data (not exported to avoid conflicts with schema's Response type)
interface PurchaseOrdersClientResponse {
  data: PurchaseOrdersRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for purchase_orders table operations
 *
 * @description Provides type-safe access to purchase_orders API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class PurchaseOrdersClient {
  private readonly basePath = '/purchase-orders';

  /**
   * Get all purchase_orders records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of purchase_orders records
   */
  async getAll(options?: PurchaseOrdersQueryOptions): Promise<PurchaseOrdersRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<PurchaseOrdersClientResponse>(url);
    return response.data;
  }

  /**
   * Get a single purchase_orders record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the purchase_orders record
   */
  async getById(id: string): Promise<PurchaseOrdersRow> {
    const response = await httpClient.get<{
      data: PurchaseOrdersRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new purchase_orders record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created purchase_orders record
   */
  async create(data: PurchaseOrdersInsert): Promise<PurchaseOrdersRow> {
    const response = await httpClient.post<{
      data: PurchaseOrdersRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing purchase_orders record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated purchase_orders record
   */
  async update(id: string, data: PurchaseOrdersUpdate): Promise<PurchaseOrdersRow> {
    const response = await httpClient.put<{
      data: PurchaseOrdersRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a purchase_orders record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple purchase_orders records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created purchase_orders records
   */
  async createMany(data: PurchaseOrdersInsert[]): Promise<PurchaseOrdersRow[]> {
    const response = await httpClient.post<{
      data: PurchaseOrdersRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple purchase_orders records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search purchase_orders with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    query: string,
    columns?: string[],
    options?: PurchaseOrdersQueryOptions
  ): Promise<PurchaseOrdersClientResponse> {
    throw new Error('Search operations are not available for purchase_orders - no searchable fields configured');
  }

  /**
   * Get count of purchase_orders records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a purchase_orders record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'purchase_orders',
      publicFields: ['id', 'organization_id', 'vendor_id', 'po_number', 'order_date', 'delivery_date', 'delivery_address', 'status', 'subtotal', 'tax', 'shipping', 'total', 'project_id', 'job_id', 'lot_id', 'notes', 'created_by', 'approved_by', 'approved_at', 'created_at', 'updated_at', 'trig_approved_by_name', 'vendor_branch_id', 'trig_vendor_name', 'trig_vendor_code', 'trig_vendor_is_active', 'trig_vendor_contact_name', 'trig_vendor_contact_email', 'trig_vendor_contact_phone', 'trig_vendor_website', 'trig_branch_name', 'trig_branch_code', 'trig_branch_display_name', 'trig_branch_contact_name', 'trig_branch_contact_email', 'trig_branch_contact_phone', 'trig_branch_address', 'trig_branch_city', 'trig_branch_state', 'trig_branch_zip_code', 'trig_branch_is_active', 'trig_branch_is_primary', 'trig_vendor_branch_name', 'phase_id', 'trig_phase_name', 'trig_phase_description', 'trig_lot_number', 'trig_created_by_name', 'status_id', 'trig_status_name', 'trig_status_code', 'trig_status_color', 'payment_terms', 'trig_job_name', 'trig_job_number', 'trig_job_builder_name', 'trig_job_city', 'auto_serial_number', 'eta', 'fees', 'discounts', 'tax_rate', 'department', 'payment_terms_id', 'trig_payment_terms_name', 'trig_payment_terms_code', 'is_pick_ticket', 'is_stock', 'vendor_order_number', 'fob', 'ship_via', 'requested_date'],
      searchableFields: [],
      filterableFields: ['id', 'organization_id', 'vendor_id', 'project_id', 'job_id', 'lot_id', 'vendor_branch_id', 'phase_id', 'status_id', 'payment_terms_id', 'is_pick_ticket', 'is_stock'],
      sortableFields: ['order_date', 'delivery_date', 'created_at', 'updated_at', 'requested_date'],
      readonlyFields: ['id', 'created_at', 'updated_at', 'trig_approved_by_name', 'trig_vendor_name', 'trig_vendor_code', 'trig_vendor_is_active', 'trig_vendor_contact_name', 'trig_vendor_contact_email', 'trig_vendor_contact_phone', 'trig_vendor_website', 'trig_branch_name', 'trig_branch_code', 'trig_branch_display_name', 'trig_branch_contact_name', 'trig_branch_contact_email', 'trig_branch_contact_phone', 'trig_branch_address', 'trig_branch_city', 'trig_branch_state', 'trig_branch_zip_code', 'trig_branch_is_active', 'trig_branch_is_primary', 'trig_vendor_branch_name', 'trig_phase_name', 'trig_phase_description', 'trig_lot_number', 'trig_created_by_name', 'trig_status_name', 'trig_status_code', 'trig_status_color', 'trig_job_name', 'trig_job_number', 'trig_job_builder_name', 'trig_job_city', 'auto_serial_number', 'trig_payment_terms_name', 'trig_payment_terms_code'],
      requiredFields: ['organization_id', 'order_date', 'created_by'],
    };
  }
}

// Export singleton instance for convenience
export const purchaseOrdersClient = new PurchaseOrdersClient();
